export interface PoiEntry {
  poiId: string;
  nameEn: string;
  descriptionEn: string;
  lat: number;
  lng: number;
}

interface RawSeed {
  collections?: {
    poi?: Record<string, unknown>;
  };
}

function extractNameEn(name: unknown, fallback: string): string {
  if (name !== null && typeof name === "object" && !Array.isArray(name)) {
    const en = (name as Record<string, unknown>).en;
    if (typeof en === "string" && en !== "") return en;
  }
  if (typeof name === "string" && name !== "") return name;
  return fallback;
}

function extractEnString(value: unknown): string {
  if (value !== null && typeof value === "object" && !Array.isArray(value)) {
    const en = (value as Record<string, unknown>).en;
    if (typeof en === "string") return en;
  }
  if (typeof value === "string") return value;
  return "";
}

export async function loadPoiSeed(): Promise<PoiEntry[]> {
  try {
    const res = await fetch("/firestore.seed.json");
    if (!res.ok) return [];
    const data = (await res.json()) as RawSeed;
    const pois = data?.collections?.poi;
    if (!pois || typeof pois !== "object") return [];
    const entries: PoiEntry[] = [];
    for (const raw of Object.values(pois)) {
      if (!raw || typeof raw !== "object") continue;
      const p = raw as Record<string, unknown>;
      const poiId = p.poiId;
      const lat = p.latitude;
      const lng = p.longitude;
      if (typeof poiId !== "string" || poiId === "") continue;
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
      entries.push({
        poiId,
        nameEn: extractNameEn(p.name, poiId),
        descriptionEn: extractEnString(p.description),
        lat: lat as number,
        lng: lng as number,
      });
    }
    return entries.sort((a, b) =>
      a.poiId.localeCompare(b.poiId, undefined, { numeric: true }),
    );
  } catch {
    return [];
  }
}

function haversineM(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6_371_000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function findNearestPoi(
  pois: PoiEntry[],
  lat: number,
  lng: number,
): { poi: PoiEntry; distM: number } | null {
  if (pois.length === 0) return null;
  let nearest = pois[0];
  let nearestDist = haversineM(lat, lng, pois[0].lat, pois[0].lng);
  for (const p of pois.slice(1)) {
    const d = haversineM(lat, lng, p.lat, p.lng);
    if (d < nearestDist) {
      nearest = p;
      nearestDist = d;
    }
  }
  return { poi: nearest, distM: nearestDist };
}
